// qs-DraggablePanel.vue
<template>
  <div
    v-if="panel?.visible"
    :data-panel-id="panelId"
    class="draggable-panel"
    :class="{
      'panel-hover': isHovered,
      'panel-dragging': isDragging,
      'panel-active': isActive
    }"
    :style="panelStyle"
    @mousedown="bringToFront"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mousemove="onMouseMove"
    @touchstart.passive="bringToFront"
  >
    <div
      class="panel-handle"
      :class="{
        'handle-visible': isHovered || isDragging,
        'handle-narrow': isNarrowPanel
      }"
      @mousedown="startDrag"
      @touchstart.passive="startDrag"
    >
      <span class="panel-title">{{ panel?.title || 'Panel' }}</span>
    </div>
    <div class="panel-content">
      <div class="panel-base-content">
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, shallowRef } from 'vue'
import { useEventListener } from '@vueuse/core'
import { usePanelStore } from '@/stores/qs-store-panels'
import type { PanelPosition } from '@/types/qs-type-panels'

interface Props {
  readonly panelId: string
}

interface Rect {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

const props = defineProps<Props>()
const panelStore = usePanelStore()

const panel = computed(() => panelStore.panels[props.panelId])
const config = computed(() => panelStore.getPanelConfig(props.panelId))

const isDragging = ref(false)
const isHovered = ref(false)
const dragStart = shallowRef<PanelPosition>({ x: 0, y: 0 })
const hoverTimeout = ref<NodeJS.Timeout | null>(null)
const localPosition = shallowRef<PanelPosition>({ x: 0, y: 0 })
const localZIndex = ref(1000)
const targetPosition = shallowRef<PanelPosition>({ x: 0, y: 0 })
const currentPosition = shallowRef<PanelPosition>({ x: 0, y: 0 })

let dragAnimationFrame: number | null = null

const isActive = computed(() => isHovered.value || isDragging.value)

const isNarrowPanel = computed(() => {
  if (!config.value) return false
  return false
})

const panelStyle = computed(() => {
  if (!panel.value || !config.value) return {}

  const position = isDragging.value ? localPosition.value : panel.value.position
  const zIndex = isDragging.value ? localZIndex.value : panel.value.zIndex

  const baseStyle: Record<string, string | number> = {
    position: 'fixed',
    left: `${position.x}px`,
    top: `${position.y}px`,
    zIndex: zIndex,
    transition: 'all 0.1s ease-out'
  }

  if (config.value.type === 'horizontal') {
    return {
      ...baseStyle,
      width: `${config.value.width}px`,
      height: typeof config.value.height === 'string' ? config.value.height : `${config.value.height}px`,
      minWidth: `${config.value.width}px`,
      maxWidth: `${config.value.width}px`,
      minHeight: typeof config.value.height === 'string' ? '100px' : undefined
    }
  }

  const width = config.value.width === 'auto' ? 'auto' : `${config.value.width}px`
  return {
    ...baseStyle,
    width: width,
    height: 'auto',
    minHeight: '20px',
    minWidth: config.value.width === 'auto' ? 'auto' : `${config.value.width}px`,
    maxWidth: config.value.width === 'auto' ? 'none' : `${config.value.width}px`
  }
})

const bringToFront = (): void => {
  panelStore.bringToFront(props.panelId)
  if (panel.value) {
    localZIndex.value = panel.value.zIndex
  }
}

const onMouseEnter = (): void => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value)
    hoverTimeout.value = null
  }

  if (isDragging.value) return

  isHovered.value = true
  panelStore.bringToFront(props.panelId)

  if (panel.value) {
    localZIndex.value = panel.value.zIndex
  }

  window.dispatchEvent(new CustomEvent('panel-hover', {
    detail: { panelId: props.panelId, isHovered: true }
  }))
}

const onMouseLeave = (event: MouseEvent): void => {
  if (isDragging.value) return

  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value)
  }

  hoverTimeout.value = setTimeout(() => {
    if (isDragging.value) return

    const relatedTarget = event.relatedTarget as HTMLElement | null
    const panelElement = event.currentTarget as HTMLElement

    if (relatedTarget && panelElement && panelElement.contains(relatedTarget)) {
      return
    }

    if (panelElement) {
      const rect = panelElement.getBoundingClientRect()
      const mouseX = event.clientX
      const mouseY = event.clientY

      if (mouseX >= rect.left && mouseX <= rect.right &&
          mouseY >= rect.top && mouseY <= rect.bottom) {
        return
      }
    }

    isHovered.value = false

    window.dispatchEvent(new CustomEvent('panel-hover', {
      detail: { panelId: props.panelId, isHovered: false }
    }))
    hoverTimeout.value = null
  }, 50)
}

const onMouseMove = (e: MouseEvent): void => {
  if (!isHovered.value) {
    e.stopPropagation()
  }
}

const checkCollision = (position: PanelPosition, excludePanelId?: string): boolean => {
  if (!config.value) return false

  const currentDimensions = panelStore.getPanelDimensions(props.panelId)
  const minDistance = 3

  const currentRect: Rect = {
    x: position.x - minDistance,
    y: position.y - minDistance,
    width: currentDimensions.width + (minDistance * 2),
    height: currentDimensions.height + (minDistance * 2)
  }

  for (const [panelId, panel] of Object.entries(panelStore.panels)) {
    if (panelId === props.panelId || panelId === excludePanelId || !panel.visible) continue

    const otherDimensions = panelStore.getPanelDimensions(panelId)
    const otherRect: Rect = {
      x: panel.position.x - minDistance,
      y: panel.position.y - minDistance,
      width: otherDimensions.width + (minDistance * 2),
      height: otherDimensions.height + (minDistance * 2)
    }

    if (currentRect.x < otherRect.x + otherRect.width &&
        currentRect.x + currentRect.width > otherRect.x &&
        currentRect.y < otherRect.y + otherRect.height &&
        currentRect.y + currentRect.height > otherRect.y) {
      return true
    }
  }

  return false
}

const findNearestValidPosition = (targetPos: PanelPosition): PanelPosition => {
  if (!config.value) return targetPos

  if (!checkCollision(targetPos)) {
    return targetPos
  }

  const stepSize = 10
  const cardinalOffsets: readonly PanelPosition[] = [
    { x: 0, y: -stepSize },
    { x: 0, y: stepSize },
    { x: -stepSize, y: 0 },
    { x: stepSize, y: 0 }
  ]

  for (const offset of cardinalOffsets) {
    for (let multiplier = 1; multiplier <= 5; multiplier++) {
      const testPos: PanelPosition = {
        x: targetPos.x + offset.x * multiplier,
        y: targetPos.y + offset.y * multiplier
      }

      const constrainedPos = panelStore.constrainToViewport(props.panelId, testPos)

      if (!checkCollision(constrainedPos)) {
        return constrainedPos
      }
    }
  }

  const diagonalOffsets: readonly PanelPosition[] = [
    { x: -stepSize, y: -stepSize },
    { x: stepSize, y: -stepSize },
    { x: -stepSize, y: stepSize },
    { x: stepSize, y: stepSize }
  ]

  for (const offset of diagonalOffsets) {
    const testPos: PanelPosition = {
      x: targetPos.x + offset.x,
      y: targetPos.y + offset.y
    }

    const constrainedPos = panelStore.constrainToViewport(props.panelId, testPos)

    if (!checkCollision(constrainedPos)) {
      return constrainedPos
    }
  }

  for (let radius = stepSize * 2; radius <= 200; radius += stepSize) {
    for (let angle = 0; angle < 360; angle += 45) {
      const rad = (angle * Math.PI) / 180
      const testPos: PanelPosition = {
        x: targetPos.x + Math.cos(rad) * radius,
        y: targetPos.y + Math.sin(rad) * radius
      }

      const constrainedPos = panelStore.constrainToViewport(props.panelId, testPos)

      if (!checkCollision(constrainedPos)) {
        return constrainedPos
      }
    }
  }

  return panelStore.constrainToViewport(props.panelId, targetPos)
}

const handleDrag = (e: MouseEvent | TouchEvent): void => {
  if (!isDragging.value) return

  if ('touches' in e) {
    e.preventDefault()
  }

  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

  const newPosition: PanelPosition = {
    x: clientX - dragStart.value.x,
    y: clientY - dragStart.value.y
  }

  const viewportConstrained = panelStore.constrainToViewport(props.panelId, newPosition)
  const collisionFreePosition = findNearestValidPosition(viewportConstrained)
  targetPosition.value = collisionFreePosition

  if (dragAnimationFrame) {
    cancelAnimationFrame(dragAnimationFrame)
  }

  const animateToTarget = (): void => {
    if (!isDragging.value) return

    const dx = targetPosition.value.x - currentPosition.value.x
    const dy = targetPosition.value.y - currentPosition.value.y
    const lerpFactor = 0.4

    currentPosition.value.x += dx * lerpFactor
    currentPosition.value.y += dy * lerpFactor
    localPosition.value = { ...currentPosition.value }

    if (isDragging.value && (Math.abs(dx) > 0.1 || Math.abs(dy) > 0.1)) {
      dragAnimationFrame = requestAnimationFrame(animateToTarget)
    }
  }

  dragAnimationFrame = requestAnimationFrame(animateToTarget)
}

const startDrag = (e: MouseEvent | TouchEvent): void => {
  if (!panel.value) return

  if ('touches' in e) {
    e.preventDefault()
  }

  isDragging.value = true
  isHovered.value = false

  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY

  dragStart.value = {
    x: clientX - panel.value.position.x,
    y: clientY - panel.value.position.y
  }

  panelStore.bringToFront(props.panelId)

  localPosition.value = { ...panel.value.position }
  currentPosition.value = { ...panel.value.position }
  targetPosition.value = { ...panel.value.position }
  localZIndex.value = panel.value.zIndex + 1000

  useEventListener(document, 'mousemove', handleDrag, { passive: true })
  useEventListener(document, 'mouseup', stopDrag, { passive: true })

  if ('ontouchstart' in window) {
    useEventListener(document, 'touchmove', handleDrag, { passive: false })
    useEventListener(document, 'touchend', stopDrag, { passive: true })
  }

  e.preventDefault()
}

const stopDrag = (): void => {
  if (!isDragging.value) return

  isDragging.value = false

  if (dragAnimationFrame) {
    cancelAnimationFrame(dragAnimationFrame)
    dragAnimationFrame = null
  }

  localPosition.value = { ...targetPosition.value }

  if (panel.value) {
    panelStore.updatePanelPosition(props.panelId, targetPosition.value)
    localZIndex.value = panel.value.zIndex
  }

  isHovered.value = false
}

onMounted(() => {
  if (Object.keys(panelStore.panels).length === 0) {
    panelStore.initializePanels()
  }

  if (panel.value) {
    localPosition.value = { ...panel.value.position }
    localZIndex.value = panel.value.zIndex
  }

  useEventListener(document, 'mouseleave', () => {
    isHovered.value = false
    window.dispatchEvent(new CustomEvent('panel-hover', {
      detail: { panelId: props.panelId, isHovered: false }
    }))
  })

  useEventListener(window, 'panel-content-changed', (event: Event) => {
    const customEvent = event as CustomEvent<{ panelId: string }>
    if (customEvent.detail.panelId === props.panelId) {
      nextTick(() => {
        panelStore.refreshPanelDimensions(props.panelId)
      })
    }
  })
})

onUnmounted(() => {
  if (dragAnimationFrame) {
    cancelAnimationFrame(dragAnimationFrame)
  }

  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value)
    hoverTimeout.value = null
  }
})
</script>

<style scoped>
.draggable-panel {
  background: transparent;
  user-select: none;
  border-radius: 0;
  will-change: transform, opacity;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  contain: layout style paint;
  display: inline-block;
  width: auto;
  min-width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  transition: background-color 0.2s ease;
}

.draggable-panel.panel-hover {
  z-index: 9999 !important;
}

.draggable-panel.panel-hover .panel-base-content {
  background: rgba(theme('colors.creme.light'), 0.15);
  border-radius: theme('borderRadius.DEFAULT');
}

.draggable-panel.panel-active {
  z-index: 9999 !important;
}

.draggable-panel.panel-active .panel-base-content {
  background: rgba(theme('colors.creme.light'), 0.25);
  border-radius: theme('borderRadius.DEFAULT');
}

.draggable-panel.panel-dragging {
  transition: left 0.1s ease-out, top 0.1s ease-out, transform 0.1s ease-out;
  will-change: transform, left, top, opacity;
  transform: translate3d(0, 0, 0);
  backface-visibility: hidden;
  contain: layout style paint;
  z-index: 99999 !important;
}

.draggable-panel.panel-dragging .panel-base-content {
  background: rgba(theme('colors.creme.light'), 0.35);
  border-radius: theme('borderRadius.DEFAULT');
  transition: background-color 0.2s ease;
}

.panel-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: move;
  width: 100%;
  height: 20px;
  padding: 4px;
  background: transparent;
  border-radius: theme('borderRadius.lg');
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease-out, background-color 0.15s ease-out, color 0.15s ease-out;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  min-width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  width: 100% !important;
  z-index: 10;
}

.panel-handle.handle-narrow {
  justify-content: center;
  padding: 0 4px;
}

.panel-handle.handle-narrow .panel-title {
  font-size: 11px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.panel-handle.handle-visible {
  opacity: 1;
  pointer-events: auto;
  background-color: theme('colors.creme.darker');
  color: theme('colors.creme.DEFAULT');
}

.panel-title {
  font-size: 10px;
  font-weight: 500;
  color: inherit;
  user-select: none;
}

.panel-content {
  background: transparent;
  border-radius: 0;
  pointer-events: auto;
  margin-top: 20px;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
}

.panel-base-content {
  background: transparent;
  border-radius: 0;
  transition: background-color 0.2s ease, border-radius 0.2s ease;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
}
</style>
